import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, Key, Square, Shield, ShieldCheck, Clock, MapPin, FileText, Wrench, Star } from "lucide-react";
import SEO from "@/components/SEO";
import DepartementsGrid from "@/components/DepartementsGrid";
import SeasonalBanner from "@/components/SeasonalBanner";
import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { VILLES } from "@/data/villes";
import { FAQ_GENERALES } from "@/data/faq";
import { AVIS, NOTE_MOYENNE, NB_AVIS } from "@/data/avis";
import { businessJsonLd, faqJsonLd } from "@/lib/jsonld";

const iconMap = { Key, Square, ShieldCheck, Blinds: Shield };

// Puteaux (base) et les communes voisines prioritaires, mises en avant avant
// la couverture régionale ; les autres villes restent listées ensuite.
const PROXIMITE_SLUGS = ["puteaux", "nanterre", "courbevoie", "la-defense", "suresnes"];
const PROXIMITE = PROXIMITE_SLUGS.map((s) => VILLES.find((v) => v.slug === s)).filter(Boolean);
const AUTRES_VILLES = VILLES.filter((v) => !PROXIMITE_SLUGS.includes(v.slug));

export default function Home() {
  return (
    <>
      <SEO
        title="Serrurier à Puteaux et alentours 24h/24 | DS Serrurier"
        description="Serrurier 24h/24 près de chez vous, basé à Puteaux (92) : Nanterre, Courbevoie, La Défense, Suresnes et toute l'Île-de-France. Ouverture de porte, cylindre A2P."
        canonical={`${SITE.siteUrl}/`}
        jsonLd={[businessJsonLd({ withReviews: true }), faqJsonLd(FAQ_GENERALES.slice(0, 4))]}
      />

      {/* ============ HERO ============ */}
      <section className="bg-navy relative overflow-hidden" data-testid="hero-section">
        {/* Motif d'arrière-plan discret */}
        <div className="absolute inset-0 opacity-[0.05]" aria-hidden="true">
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px"
          }}></div>
        </div>

        <div className="container-x relative py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 mb-6" data-testid="hero-status">
                <span className="pulse-dot"></span>
                <span className="font-mono text-xs uppercase tracking-widest" style={{ color: "var(--brass-light)" }}>
                  STATUT : SERVICE ACTIF · 24H/24 · 7J/7
                </span>
              </div>

              <h1 className="mb-6" style={{ color: "var(--white)" }} data-testid="hero-title">
                Serrurier à Puteaux<br />
                <span style={{ color: "var(--brass-light)" }}>et dans les villes voisines.</span>
              </h1>

              <p className="text-lg md:text-xl leading-relaxed mb-8 max-w-2xl" style={{ color: "rgba(255,255,255,0.85)" }}>
                Porte claquée, effraction, cylindre grippé, vitre brisée, volet bloqué. Depuis notre base de Puteaux, nous intervenons <strong style={{ color: "var(--white)" }}>7 jours sur 7, 24 heures sur 24</strong> à{" "}
                {PROXIMITE.slice(1).map((v, i, arr) => (
                  <span key={v.slug}>
                    <Link to={`/serrurier-${v.slug}`} className="underline underline-offset-4 hover:text-white" style={{ color: "var(--brass-light)" }}>{v.nom}</Link>
                    {i < arr.length - 2 ? ", " : i === arr.length - 2 ? " et " : ""}
                  </span>
                ))}
                , puis dans toute la région parisienne.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <a href={SITE.telephoneHref} className="btn btn-urgence" data-testid="hero-cta-phone">
                  <Phone size={20} strokeWidth={2.5} />
                  APPEL D'URGENCE
                </a>
                <Link to="/contact" className="btn btn-ghost-light" data-testid="hero-cta-devis">
                  <FileText size={20} strokeWidth={2.5} />
                  DEVIS EN LIGNE
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-white/10 pt-6">
                {[
                  { k: "7J/7", v: "Astreinte" },
                  { k: "24H", v: "Nuit incluse" },
                  { k: "92", v: "Base Puteaux" },
                  { k: "A2P", v: "Certifié" },
                ].map((s) => (
                  <div key={s.k}>
                    <div className="font-mono text-xl md:text-2xl font-bold" style={{ color: "var(--brass-light)" }}>{s.k}</div>
                    <div className="text-xs uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.7)" }}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-navy-2 border border-white/10 rounded-lg p-6 md:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-px" style={{ background: "var(--brass-on-dark)" }}></div>
                  <span className="font-mono text-xs uppercase tracking-widest" style={{ color: "var(--brass-on-dark)" }}>
                    ZONES DESSERVIES · CERCLE 1
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl mb-6" style={{ color: "var(--white)" }}>
                  Départements couverts
                </h2>
                <DepartementsGrid variant="compact" />
                <Link
                  to="/zones-intervention"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-heading font-bold uppercase tracking-wider hover:text-orange transition-colors"
                  style={{ color: "var(--brass-light)" }}
                  data-testid="hero-zones-link"
                >
                  Voir toutes les zones <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SeasonalBanner />

      {/* ============ SPLIT INTENTIONS ============ */}
      <section className="section-y bg-white" data-testid="intents-section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <span className="eyebrow brass">CHOISISSEZ VOTRE PARCOURS</span>
            <h2 className="mt-4 mb-4">
              Une urgence ? Un projet ?
            </h2>
            <p className="text-lg" style={{ color: "var(--ink-muted)" }}>
              Nos deux façons d'intervenir sont clairement séparées. Choisissez le parcours qui correspond à votre situation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* URGENCE */}
            <div className="intent-card urgence" data-testid="intent-urgence">
              <div className="flex items-center gap-2 mb-4">
                <span className="pulse-dot"></span>
                <span className="font-mono text-xs uppercase tracking-widest" style={{ color: "var(--orange-on-dark)" }}>
                  URGENCE IMMÉDIATE
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl mb-4" style={{ color: "var(--white)", fontFamily: "var(--font-heading)", fontWeight: 800 }}>
                Vous êtes bloqué.<br />On arrive.
              </h3>
              <ul className="space-y-2 mb-6 text-base" style={{ color: "rgba(255,255,255,0.85)" }}>
                {["Porte claquée ou verrouillée", "Effraction ou tentative", "Clé cassée dans la serrure", "Vitre brisée à sécuriser"].map((t) => (
                  <li key={t} className="flex gap-2">
                    <CheckCircle2 size={18} className="flex-shrink-0 mt-1" style={{ color: "var(--orange-on-dark)" }} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <a href={SITE.telephoneHref} className="btn btn-urgence w-full" data-testid="intent-urgence-cta">
                <Phone size={18} strokeWidth={2.5} />
                APPELER MAINTENANT
              </a>
            </div>

            {/* PROJET */}
            <div className="intent-card projet" data-testid="intent-projet">
              <div className="flex items-center gap-2 mb-4">
                <Wrench size={16} style={{ color: "var(--brass)" }} />
                <span className="font-mono text-xs uppercase tracking-widest" style={{ color: "var(--brass)" }}>
                  PROJET · RÉNOVATION
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl mb-4">
                Vous préparez<br />un chantier de sécurité.
              </h3>
              <ul className="space-y-2 mb-6 text-base" style={{ color: "var(--ink-muted)" }}>
                {["Blindage ou porte blindée", "Motorisation de volets", "Contrôle d'accès pro / copro", "Remplacement de vitrage"].map((t) => (
                  <li key={t} className="flex gap-2">
                    <CheckCircle2 size={18} className="flex-shrink-0 mt-1" style={{ color: "var(--brass)" }} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-ghost-navy w-full" data-testid="intent-projet-cta">
                <FileText size={18} strokeWidth={2.5} />
                DEMANDER UN DEVIS
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ 4 PILIERS ============ */}
      <section className="section-y bg-cream" data-testid="services-section">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div className="max-w-2xl">
              <span className="eyebrow brass">4 PILIERS · MÉTIER COMPLET</span>
              <h2 className="mt-4 mb-3">Nos services</h2>
              <p className="text-lg" style={{ color: "var(--ink-muted)" }}>
                Serrurerie, vitrerie, volets et contrôle d'accès : un seul artisan, quatre expertises complémentaires.
              </p>
            </div>
            <Link to="/services" className="btn btn-ghost-navy" data-testid="services-see-all">
              Voir tous les services <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => {
              const Icon = iconMap[s.icone] || Key;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="card-pillar block"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="icon-wrap">
                    <Icon size={22} strokeWidth={2} />
                  </div>
                  <h3 className="mb-2">{s.titre}</h3>
                  <div className="tech-line mb-3">{s.tagline}</div>
                  <p className="text-sm" style={{ color: "var(--ink-muted)" }}>{s.description.slice(0, 110)}…</p>
                  <div className="mt-4 font-heading font-bold text-sm uppercase tracking-wider inline-flex items-center gap-1 text-orange" style={{ color: "var(--orange)" }}>
                    En savoir plus <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ AVANT / APRÈS ============ */}
      <section className="section-y bg-white" data-testid="avant-apres-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <picture>
                <source
                  srcSet="/images/avant-apres-porte-reparation-640.webp 640w, /images/avant-apres-porte-reparation.webp 1250w"
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  type="image/webp"
                />
                <img
                  src="/images/avant-apres-porte-reparation.jpeg"
                  alt="Réparation de porte endommagée avant/après par DS Serrurier, Île-de-France"
                  loading="lazy"
                  width={1250}
                  height={1002}
                  className="w-full rounded-lg border"
                  style={{ borderColor: "var(--border-light)" }}
                />
              </picture>
            </div>
            <div className="lg:col-span-6">
              <span className="eyebrow brass">RÉPARATION · AVANT / APRÈS</span>
              <h2 className="mt-4 mb-4">Avant / après : remise en état d'une porte fortement endommagée</h2>
              <p className="text-lg leading-relaxed mb-6" style={{ color: "var(--ink-muted)" }}>
                Une porte abîmée ne signifie pas forcément qu'elle doit être remplacée. Nous intervenons pour réparer la structure en bois, renforcer la zone de la serrure, et poser une nouvelle poignée pour une finition propre et solide. Résultat : une porte de nouveau fonctionnelle et sécurisée, sans le coût d'un remplacement complet.
              </p>
              <span className="eyebrow mb-6">
                Intervention rapide · Dépannage 7j/7
              </span>
              <div>
                <Link to="/contact" className="btn btn-ghost-navy" data-testid="avant-apres-cta-devis">
                  <FileText size={18} strokeWidth={2.5} /> DEVIS GRATUIT
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ APERÇU ZONES / VILLES ============ */}
      <section className="section-y bg-white" data-testid="villes-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="eyebrow">UN SERRURIER PRÈS DE CHEZ VOUS</span>
              <h2 className="mt-4 mb-4">Puteaux et les villes voisines</h2>
              <p className="text-lg mb-4" style={{ color: "var(--ink-muted)" }}>
                Vous cherchez un serrurier proche de chez vous ? Notre base est à Puteaux : Nanterre, Courbevoie, La Défense et Suresnes sont nos secteurs les plus proches. Au téléphone, nous confirmons notre disponibilité et un délai estimé selon votre adresse et l'heure.
              </p>
              <p className="text-lg mb-6" style={{ color: "var(--ink-muted)" }}>
                Chaque ville a sa page, avec les situations que nous y prenons en charge et, quand nous en avons, des interventions réalisées sur place.
              </p>
              <Link to="/zones-intervention" className="btn btn-ghost-navy" data-testid="villes-see-all">
                <MapPin size={18} /> Toutes les zones
              </Link>
            </div>
            <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-3" data-testid="villes-proximite">
              {PROXIMITE.map((v) => (
                <Link
                  key={v.slug}
                  to={`/serrurier-${v.slug}`}
                  className="flex items-center justify-between gap-3 p-4 border rounded transition-all hover:shadow-md hover:-translate-y-0.5"
                  style={{ borderColor: "var(--border-light)" }}
                  data-testid={`ville-preview-${v.slug}`}
                >
                  <div>
                    <div className="font-heading font-extrabold text-lg" style={{ color: "var(--navy-deep)" }}>
                      {v.nom}
                    </div>
                    <div className="font-mono text-xs" style={{ color: "var(--brass)" }}>
                      {v.codePostal} · {v.tagline}
                    </div>
                  </div>
                  <ArrowRight size={18} style={{ color: "var(--orange)" }} />
                </Link>
              ))}
            </div>
            <div className="mt-8" data-testid="villes-autres">
              <div className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: "var(--ink-muted)" }}>
                Nous intervenons aussi à
              </div>
              <div className="flex flex-wrap gap-2">
                {AUTRES_VILLES.map((v) => (
                  <Link
                    key={v.slug}
                    to={`/serrurier-${v.slug}`}
                    className="px-3 py-2 border rounded text-sm font-heading font-bold hover:shadow-sm transition-all"
                    style={{ borderColor: "var(--border-light)", color: "var(--navy-deep)" }}
                    data-testid={`ville-preview-${v.slug}`}
                  >
                    {v.nom}
                  </Link>
                ))}
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ENGAGEMENTS ============ */}
      <section className="section-y bg-navy" data-testid="engagements-section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <span className="eyebrow light">NOS 4 ENGAGEMENTS</span>
            <h2 className="mt-4 mb-4" style={{ color: "var(--white)" }}>
              Ce que vous pouvez attendre de nous
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, titre: "Délai annoncé", texte: "Une fourchette réaliste vous est communiquée dès l'appel, en fonction de votre adresse et de l'heure." },
              { icon: FileText, titre: "Prix confirmé", texte: "Ordre de prix au téléphone, devis validé sur place avant toute intervention, aucune surprise." },
              { icon: Shield, titre: "Ouverture sans casse", texte: "Techniques non destructives privilégiées. La casse est un dernier recours, expliqué et validé." },
              { icon: CheckCircle2, titre: "Matériel A2P", texte: "Cylindres et serrures certifiés par le CNPP dès qu'un renforcement est requis." },
            ].map((e) => (
              <div key={e.titre} className="p-6 border border-white/10 rounded" data-testid={`engagement-${e.titre.toLowerCase().replace(/\s/g,"-")}`}>
                <div className="icon-wrap mb-4" style={{ background: "var(--orange)", color: "var(--white)" }}>
                  <e.icon size={22} />
                </div>
                <h3 className="mb-2" style={{ color: "var(--white)" }}>{e.titre}</h3>
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{e.texte}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section className="section-y bg-white" data-testid="process-section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <span className="eyebrow brass">DÉROULÉ · 4 ÉTAPES</span>
            <h2 className="mt-4 mb-4">Comment se passe une intervention</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 relative">
            {[
              { n: "01", t: "Appel", d: "Vous décrivez la situation. Notre standard oriente vers le bon technicien." },
              { n: "02", t: "Devis", d: "Ordre de prix au téléphone, puis devis validé de visu sur place." },
              { n: "03", t: "Intervention", d: "Ouverture, réparation ou pose selon la nature du problème. Sans casse dès que possible." },
              { n: "04", t: "Suivi", d: "Facture détaillée, garantie sur pièces et main d'œuvre. Assurance et syndics acceptés." },
            ].map((step) => (
              <div key={step.n} className="relative p-6 border rounded" style={{ borderColor: "var(--border-light)" }} data-testid={`process-step-${step.n}`}>
                <div className="font-mono text-4xl font-bold mb-2" style={{ color: "var(--brass)" }}>{step.n}</div>
                <h3 className="mb-2">{step.t}</h3>
                <p className="text-sm" style={{ color: "var(--ink-muted)" }}>{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AVIS GOOGLE (vérifiés) ============ */}
      <section className="section-y bg-cream" data-testid="avis-section">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="eyebrow brass">AVIS GOOGLE · VÉRIFIÉS</span>
              <h2 className="mt-4 mb-2">Ce que nos clients disent</h2>
            </div>
            <div className="flex items-center flex-wrap gap-3">
              <div className="flex gap-0.5" role="img" aria-label={`Note moyenne ${NOTE_MOYENNE} sur 5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={22}
                    strokeWidth={0}
                    fill={i < NOTE_MOYENNE ? "#E2531D" : "#DDE2E5"}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <div className="font-mono">
                <span className="text-lg font-bold" style={{ color: "var(--navy-deep)" }}>{NOTE_MOYENNE}/5</span>
                <span className="ml-2 text-sm" style={{ color: "var(--ink-muted)" }}>· {NB_AVIS} avis Google</span>
              </div>
              <a
                href={SITE.googleMaps}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-heading font-bold uppercase tracking-wider"
                style={{ color: "var(--orange)" }}
                data-testid="avis-google-link"
              >
                Voir tous les avis sur Google →
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {AVIS.map((a) => (
              <figure
                key={a.auteur}
                className="p-6 bg-white border rounded flex flex-col"
                style={{ borderColor: "var(--border-light)" }}
                data-testid={`avis-${a.auteur.replace(/\s/g, "-")}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5" role="img" aria-label={`${a.note} étoiles sur 5`}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        strokeWidth={0}
                        fill={i < a.note ? "#E2531D" : "#DDE2E5"}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider" style={{ color: "var(--brass)" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {a.source}
                  </div>
                </div>
                <blockquote className="text-base leading-relaxed mb-4 flex-1">
                  « {a.texte} »
                </blockquote>
                <figcaption className="pt-4 border-t" style={{ borderColor: "var(--border-light)" }}>
                  <div className="font-heading font-extrabold text-base" style={{ color: "var(--navy-deep)" }}>
                    {a.auteur}
                  </div>
                  <div className="font-mono text-xs uppercase tracking-wider mt-0.5" style={{ color: "var(--ink-muted)" }}>
                    {a.date}{a.complement ? ` · ${a.complement}` : ""}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ COURTE ============ */}
      <section className="section-y bg-white" data-testid="faq-preview-section">
        <div className="container-x max-w-4xl">
          <div className="text-center mb-10">
            <span className="eyebrow">QUESTIONS FRÉQUENTES</span>
            <h2 className="mt-4 mb-3">Ce qu'on nous demande le plus</h2>
          </div>

          <div className="space-y-3">
            {FAQ_GENERALES.slice(0, 4).map((f, i) => (
              <details key={f.q} className="group border rounded p-5 open:border-brass transition-colors" style={{ borderColor: "var(--border-light)" }} data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer font-heading font-extrabold text-base md:text-lg flex justify-between items-start gap-3">
                  <span>{f.q}</span>
                  <span className="text-orange transition-transform group-open:rotate-45" style={{ color: "var(--orange)" }}>+</span>
                </summary>
                <p className="mt-3 text-base" style={{ color: "var(--ink-muted)" }}>{f.r}</p>
              </details>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/faq" className="btn btn-ghost-navy" data-testid="faq-see-all">
              Voir toutes les questions <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ CTA FINAL ============ */}
      <section className="py-16 bg-orange" data-testid="cta-final-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h2 className="mb-2" style={{ color: "var(--white)" }}>Une porte à ouvrir maintenant ?</h2>
              <p className="text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                Notre standard est ouvert. Un technicien peut être en route dans quelques minutes.
              </p>
            </div>
            <a href={SITE.telephoneHref} className="btn bg-navy text-white hover:bg-navy-2" style={{ background: "var(--navy-deep)", color: "var(--white)" }} data-testid="cta-final-phone">
              <Phone size={20} strokeWidth={2.5} />
              APPEL DIRECT
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
